//-- Copyright (c) 2021 TMT Insights Group, LLC
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '@/_utils/axios';
import { notificationVersion } from '@/redux/selectors/configSelectors';
import { Pagination } from './slice_types';

type GetNotificationsResponse = {
  data: NotificationRecord[];
  pagination: Pagination;
};

type GetNotificationsRequest = {
  page: number;
  group_type: string;
  seen?: boolean;
  skipStatsFetch?: boolean;
};

export enum NotificationVisibility {
  ALL = 'all',
  UNSEEN = 'unseen'
}

export interface NotificationRecord {
  id: number;
  seen: boolean;
  notification: {
    id: number;
    type: string;
    group_type: string;
    message: string;
    data: {
      job_id: number;
      job_hid?: number;
      task_id: number;
      task_name: string;
      assigned_user_id: number;
      assigned_user_full_name: string;
      previous_status: string;
      new_status: string;
      processing_exception_id?: number;
      job_asset_id?: number;
    };
    created_at: string;
    updated_at: string;
  };
  created_at: string;
  updated_at: string;
}

export interface NotificationStats {
  group_types: Record<string, number>;
}

export interface NotificationState {
  loading: boolean;
  data: Record<NotificationVisibility, Record<string, NotificationRecord[]>>;
  pagination: Record<NotificationVisibility, Record<string, Pagination>>;
  settings: NotificationSettings;
  notificationTab: string | undefined;
  unseenNotificationStats: NotificationStats;
  group_types: string[];
}

export interface BaseNotificationSettingValue {
  enabled: boolean;
  description?: string;
  label?: string;
}

export interface NotificationSettingValue extends BaseNotificationSettingValue {
  group_type: string;
}

export interface NotificationSettings {
  in_app?: {
    [key: string]: NotificationSettingValue;
  };
  email?: {
    [key: string]: BaseNotificationSettingValue;
  };
}

const initialState: NotificationState = {
  data: {
    [NotificationVisibility.ALL]: {},
    [NotificationVisibility.UNSEEN]: {}
  },
  loading: false,
  notificationTab: undefined,
  pagination: {
    [NotificationVisibility.ALL]: {},
    [NotificationVisibility.UNSEEN]: {}
  },
  settings: {},
  group_types: [],
  unseenNotificationStats: {
    group_types: {}
  }
};

export const changeNotificationSeenState = createAsyncThunk(
  'notification/changeNotificationSeenState',
  async (payload: { ids?: number[]; seen?: boolean; mark_all_seen?: boolean; group_type?: string; showAll?: boolean }, { dispatch }) => {
    await axios({
      method: 'patch',
      baseURL: `/api/${notificationVersion}/`,
      url: `/notifications/user-notifications/in-app/bulk_update/`,
      data: payload
    }).then((res) => res.data);

    dispatch(getNotificationStats({ seen: false }));

    return payload;
  }
);

export const getNotificationSettings = createAsyncThunk('notification/settings', async () => {
  const data = await axios({
    method: 'get',
    baseURL: `/api/${notificationVersion}/`,
    url: `/notifications/user-notifications/settings`
  }).then((res) => res.data);
  return data;
});

export const saveNotificationSettings = createAsyncThunk('notification/saveSettings', async (settings: NotificationSettings) => {
  const data = await axios({
    method: 'put',
    baseURL: `/api/${notificationVersion}/`,
    url: `/notifications/user-notifications/settings`,
    data: settings
  }).then((res) => res.data);
  return data;
});

export const getNotifications = createAsyncThunk('notification/getNotifications', async (params: GetNotificationsRequest, { dispatch }) => {
  const { group_type, page, seen, skipStatsFetch } = params;
  const urlParams = new URLSearchParams();
  urlParams.set('page_num', page.toString());
  urlParams.set('page_size', '20');

  const data = await axios<GetNotificationsResponse>({
    method: 'post',
    baseURL: `/api/${notificationVersion}/`,
    url: `/notifications/user-notifications/in-app/?${urlParams.toString()}`,
    data: {
      group_type: [group_type],
      seen: seen === false ? seen : undefined
    }
  }).then((res) => res.data);

  if (!skipStatsFetch) {
    dispatch(getNotificationStats({ seen: false }));
  }

  return {
    data,
    group_type,
    seen
  };
});

export const getNotificationStats = createAsyncThunk(
  'notification/getNotificationStats',
  async ({ seen, skipUpdate }: { seen: boolean; skipUpdate?: boolean }) => {
    const data = await axios({
      method: 'get',
      baseURL: `/api/${notificationVersion}/`,
      url: `/notifications/user-notifications/in-app/stats/?seen=${seen ? 'true' : 'false'}`
    }).then((res) => res.data);
    return {
      ...data,
      skipUpdate
    };
  }
);

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    setNotificationTab: (state, { payload }) => {
      state.notificationTab = payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getNotifications.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getNotifications.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getNotifications.fulfilled, (state, { payload }) => {
      const {
        data: { pagination, data },
        group_type,
        seen
      } = payload;
      const visibility = seen === false ? NotificationVisibility.UNSEEN : NotificationVisibility.ALL;

      if (pagination?.page === 0) {
        state.data[visibility][group_type] = data;
      } else {
        // Only add the notification if it doesn't already exist in state.data
        const newData = data.filter(
          (notification) => !state.data[visibility][group_type].some((existingNotification) => existingNotification.id === notification.id)
        );

        state.data[visibility][group_type] = [...state.data[visibility][group_type], ...(newData || [])];
      }
      state.pagination[visibility][group_type] = pagination;
      state.loading = false;
    });
    builder.addCase(getNotificationSettings.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getNotificationSettings.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getNotificationSettings.fulfilled, (state, { payload }) => {
      state.settings = payload;
      if (payload.in_app) {
        state.group_types = Array.from(new Set(Object.keys(payload.in_app).map((key) => payload.in_app[key].group_type)));
      }
      state.loading = false;
    });
    builder.addCase(saveNotificationSettings.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(saveNotificationSettings.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(saveNotificationSettings.fulfilled, (state, { payload }) => {
      state.settings = payload;
      state.loading = false;
    });
    builder.addCase(changeNotificationSeenState.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(changeNotificationSeenState.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(changeNotificationSeenState.fulfilled, (state, { payload }) => {
      if (!state.group_types.length) return;
      const actionUnseenState = () => {
        const visibility = NotificationVisibility.UNSEEN;
        if (payload.mark_all_seen) {
          state.group_types.forEach((type) => {
            state.data[visibility][type] = [];
          });
        } else if (payload.group_type) {
          state.data[visibility][payload.group_type] = state.data[visibility][payload.group_type].filter((notif) => !payload.ids?.includes(notif.id));
        }
      };

      if (payload.showAll) {
        if (payload.mark_all_seen) {
          const visibility = NotificationVisibility.ALL;
          state.group_types.forEach((type) => {
            state.data[visibility][type] = state.data[visibility][type].map((notif) => ({
              ...notif,
              seen: true
            }));
          });
        } else if (payload.group_type) {
          const _visibility = NotificationVisibility.ALL;
          state.data[_visibility][payload.group_type] = state.data[_visibility][payload.group_type].map((notif) => ({
            ...notif,
            seen: notif.seen || payload.ids?.includes(notif.id) ? true : false
          }));
        }
      }
      actionUnseenState();

      state.loading = false;
    });
    builder.addCase(getNotificationStats.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getNotificationStats.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getNotificationStats.fulfilled, (state, { payload }) => {
      state.loading = false;
      if (!payload.skipUpdate) {
        state.unseenNotificationStats.group_types = payload.group_types;
      }
    });
  }
});

export default notificationSlice.reducer;
export const { setNotificationTab } = notificationSlice.actions;
